// Frontend RBAC Utilities
// Role-based access control helpers for UI components

export const ROLES = {
  ADMIN: 'admin',
  CUSTOMER_ADMIN: 'customer_admin',
  HR: 'hr', 
  USER: 'user'
};

// Role checking functions
export function isAdmin(user) {
  return user?.role === ROLES.ADMIN;
}

export function isCustomerAdmin(user) {
  return user?.role === ROLES.CUSTOMER_ADMIN;
}

export function isHR(user) {
  return user?.role === ROLES.HR;
}

export function isEndUser(user) {
  return user?.role === ROLES.USER;
}

export function isB2CUser(user) {
  return user?.role === ROLES.USER && (!user?.customer_id || user?.isB2C);
}

// Permission checking functions
export function canSeeAdminArea(user) {
  return isAdmin(user);
}

export function canSeeCustomerDashboard(user) {
  return isAdmin(user) || isCustomerAdmin(user) || isHR(user);
}

export function canManageUsers(user) {
  return isAdmin(user) || isCustomerAdmin(user) || isHR(user);
}

export function canManageCustomers(user) {
  return isAdmin(user);
}

export function canManageCustomerAdmins(user) {
  return isAdmin(user);
}

export function canViewReports(user) {
  return isAdmin(user) || isCustomerAdmin(user) || isHR(user);
}

export function canTakeAssessments(user) {
  return isEndUser(user);
}

// Navigation helpers
export function getAvailableRoutes(user) {
  const routes = [];
  
  // Personal routes (all users)
  if (user) {
    routes.push('/dashboard');
    
    if (isEndUser(user)) {
      routes.push('/assessment');
      routes.push('/results'); // Own results only
    }
  }
  
  // Management routes
  if (canSeeCustomerDashboard(user)) {
    if (isHR(user)) {
      routes.push('/hr-dashboard');
    }
    if (isCustomerAdmin(user)) {
      routes.push('/customer-dashboard');
    }
    routes.push('/user-management');
    routes.push('/reports');
  }
  
  // Admin routes
  if (canSeeAdminArea(user)) {
    routes.push('/admin-dashboard');
    routes.push('/customer-management');
    routes.push('/performance');
    routes.push('/settings');
  }
  
  return routes;
}

// UI component helpers
export function shouldShowNavItem(user, requiredRoles) {
  if (!user || !requiredRoles) return false;
  
  const userRole = user.role;
  return requiredRoles.includes(userRole) || isAdmin(user);
}

export function getDashboardType(user) {
  if (isAdmin(user)) return 'admin';
  if (isCustomerAdmin(user)) return 'customer_admin';
  if (isHR(user)) return 'hr';
  if (isB2CUser(user)) return 'b2c_user';
  return 'user';
}

// Redirect logic after login
export function getPostLoginRedirect(user) {
  if (isAdmin(user)) return '/admin/dashboard';
  if (isCustomerAdmin(user)) return '/customer/dashboard';
  if (isHR(user)) return '/hr/dashboard';
  if (isEndUser(user)) return '/assessment';
  return '/login';
}

// Route access validation
export function canAccessRoute(user, route) {
  const navItems = getVisibleNavItems(user);
  return navItems.some(item => item.path === route);
}

// Assessment access control
export function canStartAssessment(user, assessmentType) {
  if (!isEndUser(user)) return false;
  return ['bigfive', 'advanced'].includes(assessmentType);
}

export function canViewOwnResults(user) {
  return isEndUser(user);
}

export function canDownloadReports(user) {
  return isAdmin(user) || isCustomerAdmin(user) || isHR(user);
}

// Data access helpers
export function canAccessCustomerData(user, targetCustomerId) {
  if (isAdmin(user)) return true;
  if (!user?.customer_id) return false;
  return user.customer_id === targetCustomerId;
}

export function canAccessUserData(user, targetUserId) {
  if (isAdmin(user)) return true;
  if (user?.uid === targetUserId || user?.id === targetUserId) return true;
  return false;
}

// Navigation visibility
export function getVisibleNavItems(user) {
  const items = [];
  
  if (isEndUser(user)) {
    items.push(
      { path: '/assessment', label: 'Assessment', icon: 'clipboard', section: 'main' },
      { path: '/my-results', label: 'My Results', icon: 'chart', section: 'main' }
    );
  }
  
  if (isHR(user)) {
    items.push(
      { path: '/hr/dashboard', label: 'HR Dashboard', icon: 'users', section: 'main' },
      { path: '/reports', label: 'Reports', icon: 'file-text', section: 'main' },
      { path: '/settings', label: 'Settings', icon: 'settings', section: 'config' }
    );
  }
  
  if (isCustomerAdmin(user)) {
    items.push(
      { path: '/customer/dashboard', label: 'Customer Dashboard', icon: 'building', section: 'main' },
      { path: '/hr/dashboard', label: 'HR Dashboard', icon: 'users', section: 'main' },
      { path: '/reports', label: 'Reports', icon: 'file-text', section: 'main' },
      { path: '/user-management', label: 'User Management', icon: 'user-plus', section: 'admin' },
      { path: '/settings', label: 'Settings', icon: 'settings', section: 'config' }
    );
  }
  
  if (isAdmin(user)) {
    items.push(
      { path: '/admin/dashboard', label: 'Admin Dashboard', icon: 'shield', section: 'main' },
      { path: '/admin/performance', label: 'Performance', icon: 'activity', section: 'main' },
      { path: '/admin/api-management', label: 'API Management', icon: 'settings', section: 'main' },
      { path: '/customer/dashboard', label: 'Customer Dashboard', icon: 'building', section: 'main' },
      { path: '/hr/dashboard', label: 'HR Dashboard', icon: 'users', section: 'main' },
      { path: '/reports', label: 'Reports', icon: 'file-text', section: 'main' },
      { path: '/user-management', label: 'User Management', icon: 'user-plus', section: 'admin' },
      { path: '/settings', label: 'Settings', icon: 'settings', section: 'config' }
    );
  }
  
  return items;
}

// Organization switcher helpers
export function canSwitchOrganization(user) {
  return isAdmin(user);
}

export function getOrganizationSwitcherData(user, selectedCustomerId = null) {
  if (!isAdmin(user)) return null;
  
  return {
    canSwitch: true,
    selectedCustomerId,
    placeholder: 'Select Organization...'
  };
}

// Dashboard type detection
export function getDashboardConfig(user) {
  if (isAdmin(user)) {
    return {
      type: 'admin',
      title: 'Admin Dashboard',
      subtitle: '',
      showOrgSwitcher: false
    };
  }
  
  if (isCustomerAdmin(user)) {
    return {
      type: 'customer_admin',
      title: 'Customer Dashboard',
      subtitle: 'Organization overview and management',
      showOrgSwitcher: false
    };
  }
  
  if (isHR(user)) {
    return {
      type: 'hr',
      title: 'HR Dashboard',
      subtitle: 'Employee assessment tracking',
      showOrgSwitcher: false
    };
  }
  
  return {
    type: 'user',
    title: 'Assessment Portal',
    subtitle: 'Complete your personality and productivity assessments',
    showOrgSwitcher: false
  };
}